import { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import toast from "react-hot-toast";

import Table from "../../components/Tables/Table";
import TableHeader from "../../components/Tables/TableHeader";

import { getSessions } from "../../services/sessionService";
import { getGraduationStatistics } from "../../services/graduationStatisticsService";

const { Option } = Select;

const GraduationStatsTab = ({ institution }) => {
  const [stats, setStats] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState("Not Selected");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedRowKey, setExpandedRowKey] = useState(null);

  useEffect(() => {
    if (!institution?.id) return;
    loadAll(institution.id);
  }, [institution?.id]);

  const loadAll = async (institutionId) => {
    setLoading(true);
    try {
      const [sessRes, statRes] = await Promise.all([
        getSessions(),
        getGraduationStatistics(),
      ]);

      const allSessions = sessRes?.data?.data || [];
      const allStats = statRes?.data?.data || [];

      // scope stats to this institution
      const scoped = allStats.filter(
        (s) => s.institution?.id === institutionId
      );

      setSessions(allSessions);
      setStats(scoped);
      setFilteredStats(scoped);
    } catch {
      toast.error("Failed to load graduation statistics");
    } finally {
      setLoading(false);
    }
  };

  // Filters (search on year or level)
  useEffect(() => {
    let filtered = stats;

    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((row) => {
        const sessionName = (row.session?.name || "").toLowerCase();
        const levelName = (
          row.academic_program_level?.name || ""
        ).toLowerCase();
        return sessionName.includes(term) || levelName.includes(term);
      });
    }

    if (selectedSession !== "Not Selected") {
      filtered = filtered.filter(
        (item) => item.session?.id?.toString() === selectedSession?.toString()
      );
    }

    setFilteredStats(filtered);
  }, [searchTerm, selectedSession, stats]);

  // Group by Session (totals across levels)
  const groupedStats = useMemo(() => {
    const grouped = new Map();
    for (const item of filteredStats) {
      const sess = item.session;
      if (!sess) continue;

      const key = `${institution.id}__${sess.id}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          session: sess,
          data: [],
          totals: {
            humanities_male_count: 0,
            humanities_female_count: 0,
            sciences_male_count: 0,
            sciences_female_count: 0,
            total: 0,
          },
        });
      }
      const grp = grouped.get(key);
      grp.data.push(item);

      const hm = item.humanities_male_count || 0;
      const hf = item.humanities_female_count || 0;
      const sm = item.sciences_male_count || 0;
      const sf = item.sciences_female_count || 0;

      grp.totals.humanities_male_count += hm;
      grp.totals.humanities_female_count += hf;
      grp.totals.sciences_male_count += sm;
      grp.totals.sciences_female_count += sf;
      grp.totals.total += hm + hf + sm + sf;
    }
    return Array.from(grouped.values());
  }, [filteredStats, institution]);

  const columns = [
    { title: "Year", dataIndex: ["session", "name"], width: 160 },
    {
      title: "Male (Humanities)",
      dataIndex: ["totals", "humanities_male_count"],
      width: 160,
    },
    {
      title: "Female (Humanities)",
      dataIndex: ["totals", "humanities_female_count"],
      width: 160,
    },
    {
      title: "Male (Sciences)",
      dataIndex: ["totals", "sciences_male_count"],
      width: 160,
    },
    {
      title: "Female (Sciences)",
      dataIndex: ["totals", "sciences_female_count"],
      width: 160,
    },
    { title: "Total", dataIndex: ["totals", "total"], width: 140 },
  ];

  return (
    <div className="pt-2">
      <Table
        isPaginable
        pageSize={10}
        isSearchable={false}
        loading={loading}
        columns={columns}
        rows={groupedStats}
        expandable={{
          expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
          onExpand: (expanded, record) => {
            setExpandedRowKey(expanded ? record.key : null);
          },
          expandedRowRender: (record) => (
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2">Level</th>
                  <th className="text-left p-2">Male (Humanities)</th>
                  <th className="text-left p-2">Female (Humanities)</th>
                  <th className="text-left p-2">Male (Sciences)</th>
                  <th className="text-left p-2">Female (Sciences)</th>
                  <th className="text-left p-2">Total</th>
                </tr>
              </thead>
              <tbody className="bg-amber-50">
                {record.data.map((item) => {
                  const rowTotal =
                    (item.humanities_male_count || 0) +
                    (item.humanities_female_count || 0) +
                    (item.sciences_male_count || 0) +
                    (item.sciences_female_count || 0);

                  return (
                    <tr key={item.id}>
                      <td className="p-2 text-sm">
                        {item.academic_program_level?.name || "—"}
                      </td>
                      <td className="p-2 text-sm">
                        {item.humanities_male_count}
                      </td>
                      <td className="p-2 text-sm">
                        {item.humanities_female_count}
                      </td>
                      <td className="p-2 text-sm">
                        {item.sciences_male_count}
                      </td>
                      <td className="p-2 text-sm">
                        {item.sciences_female_count}
                      </td>
                      <td className="p-2 text-sm">{rowTotal}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ),
        }}
        extra={
          <div className="mb-2 py-2 flex gap-2">
            <Select
              multiple={false}
              id="sessions"
              defaultValue={selectedSession}
              className="w-full h-[36px] min-w-[250px]"
              onChange={(value) => setSelectedSession(value)}
              showSearch
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option value={"Not Selected"}>Filter by Year</Option>
              {sessions.map((s) => (
                <Option key={s.id} value={s.id}>
                  {s.name}
                </Option>
              ))}
            </Select>
          </div>
        }
      />
    </div>
  );
};

export default GraduationStatsTab;
